/**
 * D2 — the adoption review's form-level proposals: the dictionary's question
 * order and its repeating blocks, and the two things it reports but never
 * moves.
 *
 * Pure; `web` has no component runner, so the wording and the tick rules live
 * here and are tested by `formProposalsDisplay.test.ts`.
 *
 * Every proposal starts ticked (Jim, 2026-09-16: "proposed per form as a ticked
 * item you can untick"). The page must send the ticked keys: the server applies
 * NONE of these when a list is absent.
 */

/** Mirrors functions/src/dataDictionary/adoptDictionary.ts. */
export interface ReorderProposal {
  formId: string;
  formName: string;
  formCode: string;
  sectionIndex: number;
  sectionTitle: string;
  from: string[];
  to: string[];
  moved: number;
}

export interface RepeatBlockProposal {
  formId: string;
  formName: string;
  formCode: string;
  sectionIndex: number;
  sectionTitle: string;
  pageName: string;
  label: string;
  max?: number;
}

export interface RepeatUnplacedReport {
  formId: string;
  formName: string;
  pageName: string;
  sectionTitles: string[];
}

export interface CrossSectionReport {
  formId: string;
  formName: string;
  pageName: string;
  variables: string[];
}

type SectionRef = { formId: string; sectionIndex: number };

/** The server's key for a section: `${formId}#${sectionIndex}`. */
export function sectionKey(p: SectionRef): string {
  return `${p.formId}#${p.sectionIndex}`;
}

/** Every proposal ticked. */
export function initialSectionTicks(list: SectionRef[] | undefined): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const p of list ?? []) out[sectionKey(p)] = true;
  return out;
}

/**
 * The keys to send. Always a list — an empty one when nothing is ticked —
 * because the server reads an ABSENT list as "the page predates this".
 */
export function tickedKeys(list: SectionRef[] | undefined, ticks: Record<string, boolean>): string[] {
  return (list ?? []).map(sectionKey).filter((k) => ticks[k]);
}

function where(p: { formName: string; sectionTitle: string; sectionIndex: number }): string {
  const section = p.sectionTitle && p.sectionTitle.trim()
    ? p.sectionTitle.trim()
    : `section ${p.sectionIndex + 1}`;
  return `${p.formName} › ${section}`;
}

/** "and"-joined list: "A, B and C". */
function listed(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export function reorderLine(r: ReorderProposal): string {
  const n = r.moved;
  return `${where(r)}: ${n} ${n === 1 ? 'question moves' : 'questions move'} into the dictionary's order`;
}

export function repeatLine(b: RepeatBlockProposal): string {
  const limit = typeof b.max === 'number' && b.max > 0 ? `, up to ${b.max}` : '';
  return `${where(b)}: complete once for each ${b.label}${limit}`;
}

export function unplacedLine(u: RepeatUnplacedReport): string {
  return `${u.formName}: the dictionary's page "${u.pageName}" is spread over ` +
    `${listed(u.sectionTitles)} on the form, so it was not marked as one block. ` +
    'Nothing was moved.';
}

export function crossSectionLine(c: CrossSectionReport): string {
  return `${c.formName}: on the dictionary's page "${c.pageName}", ` +
    `${c.variables.join(', ')} belong in another section of the form. ` +
    'They are left where they are.';
}
